import { createAxiosClient } from "./createAxiosClient";
import { store } from "../store";
import {
  setAuthTokens,
  setUser,
  setUserDetails,
} from "../reducers/authReducer";
import { setProgress } from "../reducers/fileTransferReducer";
import { jwtDecode } from "jwt-decode";
import { baseUrl } from "utils/backendConfig";
import { toast, Slide } from "react-toastify";
import i18n from "../i18n";

const refreshTokenUrl = baseUrl + "token/refresh/";

function getCurrentAccessToken() {
  let tokens = store.getState().auth.authTokens;
  return tokens ? tokens.access : null;
}

function getCurrentRefreshToken() {
  let tokens = store.getState().auth.authTokens;
  return tokens ? tokens.refresh : null;
}

function setRefreshedTokens(tokens) {
  store.dispatch(setAuthTokens(tokens));
  store.dispatch(setUser(jwtDecode(tokens.access)));
}

async function logout() {
  store.dispatch(setAuthTokens(null));
  store.dispatch(setUser(null));
  store.dispatch(setUserDetails(null));
}

function handleImplementationMismatch(serverImplementation) {
  const state = store.getState();
  const localImplementation = state.auth.userDetails?.implementation_code;

  if (localImplementation && serverImplementation !== localImplementation) {
    console.warn(
      `Implementation mismatch detected: server=${serverImplementation}, local=${localImplementation}. Clearing user details.`,
    );
    store.dispatch(setUserDetails(null));

    if (
      window.location.pathname !== "/welcome" &&
      window.location.pathname !== "/auth/login"
    ) {
      window.location.href = "/welcome";
    }
  }
}

// Ressincronização de acesso (RolesV2): o backend carimba o header
// ``X-Perms-Stamp`` em toda resposta com a última alteração de acesso do
// usuário (role/permissões/scope). Comparamos com o valor recebido no último
// ``post_login_info`` (``userDetails.permissions_stamp``). Se divergir, as
// permissões mudaram no servidor — limpamos ``userDetails`` e redirecionamos
// para ``/welcome``, que re-busca tudo de forma transparente (sem logout).
function handlePermsStampChange(serverStamp) {
  if (!serverStamp) return;

  const state = store.getState();
  const userDetails = state.auth.userDetails;
  // Sem userDetails (ex.: já em /welcome re-buscando) → nada a comparar.
  if (!userDetails) return;

  const localStamp = userDetails.permissions_stamp;
  // Sem baseline local (payload antigo, anterior a este recurso) → não age.
  if (localStamp === undefined || localStamp === null) return;

  if (serverStamp !== localStamp) {
    console.warn(
      `Permissions stamp changed: server=${serverStamp}, local=${localStamp}. Re-syncing access.`,
    );
    store.dispatch(setUserDetails(null));

    if (
      window.location.pathname !== "/welcome" &&
      window.location.pathname !== "/auth/login"
    ) {
      window.location.href = "/welcome";
    }
  }
}

// Aviso amigavel quando uma ACAO e' negada por falta de permissao (403
// ``not_allowed_*``). Throttle simples evita spam quando varias requests
// paralelas falham com 403 ao mesmo tempo (ex.: uma tela que dispara N
// chamadas). A mensagem usa i18n (chave ``errors.permission_denied``).
let lastPermissionToastAt = 0;

function handlePermissionDenied() {
  const now = Date.now();
  if (now - lastPermissionToastAt < 2000) return;
  lastPermissionToastAt = now;

  const message = i18n.t("permission_denied", {
    defaultValue: "You don't have permission to perform this action.",
  });

  toast(message, {
    position: "top-right",
    autoClose: 4000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: false,
    theme: "light",
    type: "warning",
    transition: Slide,
  });
}

export const client = createAxiosClient({
  options: {
    baseURL: baseUrl,
    timeout: 120000,
    headers: {
      "Content-Type": "application/json",
    },
  },
  getCurrentAccessToken,
  getCurrentRefreshToken,
  refreshTokenUrl: refreshTokenUrl,
  logout,
  setRefreshedTokens,
  onImplementationMismatch: handleImplementationMismatch,
  onPermsStampChange: handlePermsStampChange,
  onPermissionDenied: handlePermissionDenied,
});

export const fileUploadClient = createAxiosClient({
  options: {
    baseURL: baseUrl,
    timeout: 600000,
    headers: {
      "Content-Type": "multipart/form-data",
    },
    onUploadProgress: (progressEvent) => {
      const progress = Math.round(
        (100 * progressEvent.loaded) / progressEvent.total,
      );
      store.dispatch(setProgress(progress));
    },
  },
  getCurrentAccessToken,
  getCurrentRefreshToken,
  refreshTokenUrl: refreshTokenUrl,
  logout,
  setRefreshedTokens,
  onImplementationMismatch: handleImplementationMismatch,
  onPermsStampChange: handlePermsStampChange,
  onPermissionDenied: handlePermissionDenied,
});

export const fileDownloadClient = createAxiosClient({
  options: {
    baseURL: baseUrl,
    timeout: 300000,
    responseType: "blob",
  },
  getCurrentAccessToken,
  getCurrentRefreshToken,
  refreshTokenUrl: refreshTokenUrl,
  logout,
  setRefreshedTokens,
  onImplementationMismatch: handleImplementationMismatch,
  onPermsStampChange: handlePermsStampChange,
  onPermissionDenied: handlePermissionDenied,
});
